// src/common/utils/enumToIcon.ts
import {
  MdAddShoppingCart,
  MdRestaurant,
  MdDeliveryDining,
  MdCheckCircle,
  MdStore,
  MdCancel,
  MdHelp,
  MdCreditCard,
  MdMoney,
  MdPayment,
  MdSchedule,
  MdError,
  MdRefresh,
  MdLocalFlorist,
  MdBuild,
  MdCheckroom,
  MdViewModule,
  MdDoorFront,
  MdOilBarrel,
  MdFace,
  MdWeekend,
  MdDirectionsCar,
  MdAutorenew,
  MdInventory,
  MdMedicalServices,
  MdCameraAlt,
  MdCarRepair,
  MdSpa,
} from "react-icons/md";
import { IconType } from "react-icons";
import {
  OrderStatus,
  DeliveryType,
  PaymentMethod,
  PaymentStatus,
  ClusterTypes,
} from "../../types/enums";

// Функции для получения иконок
export const getOrderStatusIcon = (status: OrderStatus): IconType => {
  switch (Number(status)) {
    case Number(OrderStatus.NEW):
      return MdAddShoppingCart;
    case Number(OrderStatus.PREPARING):
      return MdAutorenew;
    case Number(OrderStatus.DELIVERING):
      return MdDeliveryDining;
    case Number(OrderStatus.DELIVERED):
      return MdCheckCircle;
    case Number(OrderStatus.PICKED_UP):
      return MdStore;
    case Number(OrderStatus.CANCELLED):
      return MdCancel;
    case OrderStatus.READY:
      return MdInventory; // ящик/коробка
    default:
      return MdHelp;
  }
};

export const getDeliveryTypeIcon = (type: DeliveryType): IconType => {
  switch (Number(type)) {
    case Number(DeliveryType.DELIVERY):
      return MdDeliveryDining;
    case Number(DeliveryType.PICKUP):
      return MdStore;
    default:
      return MdHelp;
  }
};

export const getPaymentMethodIcon = (method: PaymentMethod): IconType => {
  switch (Number(method)) {
    case Number(PaymentMethod.CARD_ON_DELIVERY):
      return MdCreditCard;
    case Number(PaymentMethod.CASH_ON_DELIVERY):
      return MdMoney;
    case Number(PaymentMethod.ONLINE):
      return MdPayment;
    default:
      return MdHelp;
  }
};

export const getPaymentStatusIcon = (status: PaymentStatus): IconType => {
  switch (Number(status)) {
    case Number(PaymentStatus.PENDING):
      return MdSchedule;
    case Number(PaymentStatus.PAID):
      return MdCheckCircle;
    case Number(PaymentStatus.FAILED):
      return MdError;
    case Number(PaymentStatus.REFUNDED):
      return MdRefresh;
    default:
      return MdHelp;
  }
};

// Функции для получения цвета иконки (возвращаем строки с цветами)
export const getOrderStatusIconColor = (status: OrderStatus): string => {
  switch (Number(status)) {
    case Number(OrderStatus.NEW):
      return "#3498db"; // синий
    case Number(OrderStatus.PREPARING):
      return "#f39c12"; // оранжевый
    case Number(OrderStatus.DELIVERING):
      return "#9b59b6"; // фиолетовый
    case Number(OrderStatus.DELIVERED):
      return "#2ecc71"; // зеленый
    case Number(OrderStatus.PICKED_UP):
      return "#27ae60"; // темно-зеленый
    case Number(OrderStatus.CANCELLED):
      return "#e74c3c"; // красный
    default:
      return "#95a5a6"; // серый
  }
};

export const getPaymentStatusIconColor = (status: PaymentStatus): string => {
  switch (Number(status)) {
    case Number(PaymentStatus.PENDING):
      return "#f39c12"; // оранжевый
    case Number(PaymentStatus.PAID):
      return "#2ecc71"; // зеленый
    case Number(PaymentStatus.FAILED):
      return "#e74c3c"; // красный
    case Number(PaymentStatus.REFUNDED):
      return "#95a5a6"; // серый
    default:
      return "#95a5a6"; // серый
  }
};

// Функции для получения всех значений enum с иконками
export const getOrderStatusOptionsWithIcons = () => {
  return Object.values(OrderStatus)
    .filter((value) => typeof value === "number")
    .map((status) => ({
      value: status as OrderStatus,
      label: String(status),
      icon: getOrderStatusIcon(status as OrderStatus),
      color: getOrderStatusIconColor(status as OrderStatus),
    }));
};

export const getDeliveryTypeOptionsWithIcons = () => {
  return Object.values(DeliveryType)
    .filter((value) => typeof value === "number")
    .map((type) => ({
      value: type as DeliveryType,
      label: String(type),
      icon: getDeliveryTypeIcon(type as DeliveryType),
    }));
};

export const getPaymentMethodOptionsWithIcons = () => {
  return Object.values(PaymentMethod)
    .filter((value) => typeof value === "number")
    .map((method) => ({
      value: method as PaymentMethod,
      label: String(method),
      icon: getPaymentMethodIcon(method as PaymentMethod),
    }));
};

export const getClusterTypeIcon = (
  clusterType?: ClusterTypes | null | string,
): IconType => {
  if (clusterType == null || String(clusterType).trim() === "") {
    return MdViewModule;
  }

  const numericValue = Number(clusterType);

  switch (numericValue) {
    case ClusterTypes.FOOD: // 0
      return MdRestaurant;
    case ClusterTypes.FLOWERS: // 1
      return MdLocalFlorist;
    case ClusterTypes.DIY: // 2
      return MdBuild;
    case ClusterTypes.CLOTHES: // 3
      return MdCheckroom;
    case ClusterTypes.FURNITURE: // 4
      return MdWeekend;
    case ClusterTypes.DOORS: // 5
      return MdDoorFront;
    case ClusterTypes.DEALER: // 6
      return MdDirectionsCar;
    case ClusterTypes.OIL: // 7
      return MdOilBarrel;
    case ClusterTypes.COSMETICS: // 8
      return MdFace;
    case ClusterTypes.BEAUTY_SALON: // 9
      return MdSpa; // или MdSpa
    case ClusterTypes.MEDICINE: // 10
      return MdMedicalServices;
    case ClusterTypes.PHOTO_STUDIO: // 11
      return MdCameraAlt;
    case ClusterTypes.AUTO_PARTS: // 12
      return MdCarRepair;
    default:
      return MdOilBarrel;
  }
};
